import React, { useEffect, useRef, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import * as stateActions from '../../actions/stateActions';

import { styled, keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicNone from '@mui/icons-material/MicNone';
import MicOff from '@mui/icons-material/MicOff';
import PersonIcon from '@mui/icons-material/Person';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { useLocalMedia } from '../../contexts/MediaSipContext';

// Smooth animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(255, 23, 68, 0.3); }
  50% { box-shadow: 0 0 16px rgba(255, 23, 68, 0.6); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Container that matches Dialer's spacing and alignment
const Container = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    animation: `${fadeInUp} 0.5s ease-out`,
}));

// Video container with subtle modern styling to match Dialer's Paper component
const ButtonContainer = styled(Box)(() => ({
    position: 'absolute',
    bottom: '25px',
    background: 'transparent'
}));

// Video display area
const VideoBox = styled(Box)(({ theme, hasVideo }) => ({
    width: '100%',
    aspectRatio: '16/9',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '16px',
    background: hasVideo
        ? '#000'
        : theme.palette.grey[900],
    overflow: 'hidden',
    '& video': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    }
}));

// No video overlay with consistent styling
const NoVideoOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginBottom: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`,
    color: theme.palette.grey[400],
    gap: theme.spacing(1),
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
        transform: 'translateX(-100%)',
        animation: `${shimmer} 2s ease-in-out infinite`,
    },
    [theme.breakpoints.down('sm')]: {
        marginBottom: '20px'
    }
}));

// Status chip matching the design language
const StatusChip = styled(Chip)(({ theme, isLive }) => ({
    position: 'absolute',
    top: 12,
    right: 12,
    background: isLive
        ? theme.palette.error.main
        : theme.palette.grey[600],
    color: 'white',
    fontSize: '0.75rem',
    height: 24,
    fontWeight: 500,
    animation: isLive ? `${pulseGlow} 2s ease-in-out infinite` : 'none',
    zIndex: 2,
    '& .MuiChip-label': {
        padding: theme.spacing(0, 1),
    }
}));

// Controls section matching Dialer's button layout
const ControlsSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    width: 'fit-content',
}));

const ControlButton = styled(IconButton)(({ theme, isMuted }) => ({
    width: 100,
    height: 50,
    borderRadius: 22,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    boxShadow: theme.shadows[2],
    transition: 'all 0.2s ease-in-out',
    backgroundColor: isMuted ? theme.palette.error.main : theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        boxShadow: theme.shadows[4],
        transform: 'translateY(-1px)',
        backgroundColor: isMuted ? theme.palette.error.dark : theme.palette.primary.dark,
    },
    '&:active': {
        transform: 'translateY(0)',
    },
    '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        boxShadow: theme.shadows[1],
    },
    [theme.breakpoints.down('sm')]: {
        width: '80px',
        height: '40px',
        minWidth: 'unset',
    }
}));

// Divider with spacing to match Dialer's layout
const SectionDivider = styled(Divider)(({ theme }) => ({
    width: '100%',
    margin: theme.spacing(2, 0, 1.3, 0),
    opacity: 0.8,
}));

function LocalVideoPreviewer() {
    const videoRef = useRef(null);
    const dispatch = useDispatch();
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const { localStream, hasVideo, toggleVideo, toggleAudio } = useLocalMedia();
    const { audioEnabled, videoEnabled } = useSelector(state => state.user);

    // Sync Redux state with actual stream state on mount/reload
    // This ensures buttons reflect the actual state after page reload
    useEffect(() => {
        if (localStream) {
            const audioTracks = localStream.getAudioTracks();
            const videoTracks = localStream.getVideoTracks();
            const actualAudioEnabled = audioTracks.length > 0 && audioTracks.some(track => track.enabled && track.readyState === 'live');
            const actualVideoEnabled = videoTracks.length > 0 && videoTracks.some(track => track.enabled && track.readyState === 'live');
            
            // Sync Redux state if it doesn't match actual stream state
            if (actualAudioEnabled !== audioEnabled) {
                dispatch(stateActions.setAudioEnabled(actualAudioEnabled));
            }
            
            if (actualVideoEnabled !== videoEnabled) {
                dispatch(stateActions.setVideoEnabled(actualVideoEnabled));
            }
        } else {
            // No stream - ensure Redux state reflects this (buttons should be off)
            if (audioEnabled) {
                dispatch(stateActions.setAudioEnabled(false));
            }
            if (videoEnabled) {
                dispatch(stateActions.setVideoEnabled(false));
            }
        }
    }, [localStream, audioEnabled, videoEnabled, dispatch]); // Only run when localStream changes (on mount/reload)

    // Update video element when stream changes
    useEffect(() => {
        const updateVideoElement = async () => {
            const videoElement = videoRef.current;
            if (!videoElement) return;

            const shouldShowVideo = Boolean(localStream && videoEnabled && hasVideo);

            if (shouldShowVideo) {
                try {
                    if (videoElement.srcObject !== localStream) {
                        videoElement.srcObject = localStream;
                    }

                    try {
                        await videoElement.play();
                        setIsVideoPlaying(true);
                    } catch (playError) {
                        console.warn('Video autoplay failed (expected in some browsers):', playError);
                        setIsVideoPlaying(false);
                    }
                } catch (error) {
                    console.error('Error setting video source:', error);
                    setIsVideoPlaying(false);
                }
            } else {
                if ('srcObject' in videoElement && videoElement.srcObject) {
                    videoElement.srcObject = null;
                } else if (videoElement.src) {
                    videoElement.src = '';
                }
                setIsVideoPlaying(false);
            }
        };
        updateVideoElement();
    }, [localStream, videoEnabled, hasVideo]);

    // Handle video element events
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handlePlay = () => setIsVideoPlaying(true);
        const handlePause = () => setIsVideoPlaying(false);
        const handleError = (e) => {
            console.error('Video element error:', e);
            setIsVideoPlaying(false);
        };

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('error', handleError);

        return () => {
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('error', handleError);
        };
    }, []);

    // Handle audio toggle with proper event handler
    const handleAudioToggle = useCallback(async () => {
        try {
            const newAudioEnabled = !audioEnabled;
            console.log('handleAudioToggle: Toggling audio to', newAudioEnabled);
            
            // Update Redux state first for immediate UI feedback
            dispatch(stateActions.toggleAudio());

            // Use the context's toggleAudio which properly handles stream management
            // toggleAudio will create stream if needed when enabling
            if (toggleAudio) {
                try {
                    await toggleAudio(newAudioEnabled);
                    console.log('toggleAudio completed successfully');
                } catch (error) {
                    console.error('Error in toggleAudio:', error);
                    // Revert Redux state if toggle failed
                    dispatch(stateActions.toggleAudio());
                }
            } else {
                console.warn('toggleAudio function not available');
                // Revert if toggleAudio not available
                dispatch(stateActions.toggleAudio());
            }
        } catch (error) {
            console.error('Error toggling audio:', error);
            // Revert toggle on error
            dispatch(stateActions.toggleAudio());
        }
    }, [dispatch, audioEnabled, toggleAudio]);

    // Handle video toggle with proper event handler
    const handleVideoToggle = useCallback(async () => {
        try {
            const newVideoEnabled = !videoEnabled;
            console.log('handleVideoToggle: Toggling video to', newVideoEnabled);
            console.log('handleVideoToggle: Current state - videoEnabled:', videoEnabled, 'hasVideo:', hasVideo, 'localStream:', !!localStream);
            
            // Update Redux state first for immediate UI feedback
            dispatch(stateActions.toggleVideo());

            // Use the context's toggleVideo which properly handles stream management
            // toggleVideo will create stream if needed when enabling
            if (toggleVideo) {
                try {
                    await toggleVideo(newVideoEnabled);
                    console.log('toggleVideo completed successfully');
                } catch (error) {
                    console.error('Error in toggleVideo:', error);
                    // Revert Redux state if toggle failed
                    dispatch(stateActions.toggleVideo());
                }
            } else {
                console.warn('toggleVideo function not available');
                // Revert if toggleVideo not available
                dispatch(stateActions.toggleVideo());
            }
        } catch (error) {
            console.error('Error toggling video:', error);
            // Revert toggle on error
            dispatch(stateActions.toggleVideo());
        }
    }, [dispatch, videoEnabled, toggleVideo, localStream, hasVideo]);

    const getNoVideoMessage = useCallback(() => {
        if (!localStream) {
            return {
                title: 'No Media Stream',
                subtitle: 'Please enable camera and microphone access'
            };
        }
        if (!hasVideo) {
            return {
                title: 'No Camera Access',
                subtitle: 'Camera permission denied or no camera device available'
            };
        }
        if (!videoEnabled) {
            return {
                title: 'Camera Disabled',
                subtitle: 'Click the camera button to enable video preview'
            };
        }
        return {
            title: 'Camera Not Available',
            subtitle: 'Please check your camera connection'
        };
    }, [localStream, hasVideo, videoEnabled]);

    const noVideoMessage = getNoVideoMessage();
    const showVideo = localStream && hasVideo && videoEnabled;
    const isStreamActive = localStream && localStream.active;

    return (
        <Container>
            {/* Section divider to separate from call input */}
            <SectionDivider />

            <VideoBox hasVideo={showVideo}>
                {showVideo ? (
                    <>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted // Always mute preview to avoid feedback
                        />
                        <StatusChip
                            label={isVideoPlaying ? "LIVE" : "READY"}
                            size="small"
                            isLive={isStreamActive && isVideoPlaying}
                        />
                    </>
                ) : (
                    <NoVideoOverlay>
                        <PersonIcon sx={{ fontSize: 60, opacity: 0.6, mb: 1 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                textAlign: 'center',
                                fontSize: '1.1rem'
                            }}
                        >
                            {noVideoMessage.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                opacity: 0.7,
                                textAlign: 'center',
                                fontSize: '0.875rem',
                                maxWidth: '300px'
                            }}
                        >
                            {noVideoMessage.subtitle}
                        </Typography>
                    </NoVideoOverlay>
                )}
            </VideoBox>

            {/* Controls using Dialer's ActionButton styling pattern */}
            <ButtonContainer>
                <ControlsSection>
                    <ControlButton
                        onClick={handleAudioToggle}
                        isMuted={!audioEnabled}
                        disabled={false}
                        title={
                            !audioEnabled
                                ? 'Enable microphone'
                                : 'Disable microphone'
                        }
                        aria-label={audioEnabled ? 'Disable microphone' : 'Enable microphone'}
                    >
                        {!audioEnabled ? <MicOff /> : <MicNone />}
                    </ControlButton>

                    <ControlButton
                        onClick={handleVideoToggle}
                        isMuted={!videoEnabled}
                        disabled={false}
                        title={
                            !videoEnabled
                                    ? 'Enable camera'
                                    : 'Disable camera'
                        }
                        aria-label={videoEnabled ? 'Disable camera' : 'Enable camera'}
                    >
                        {!videoEnabled ? <VideocamOffIcon /> : <VideocamIcon />}
                    </ControlButton>
                </ControlsSection>
            </ButtonContainer>
        </Container>
    )
}

export default LocalVideoPreviewer
